/**
 * 組織情報・組織階層のマスタ設定画面。 期間は useDepartmentInitialization で一覧のみ先に確定し、明細（組織一覧・階層）は currentPeriodKey 確定後の loadPeriodData
 * で遅延取得する二段構成。
 */
import * as React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as UndoIcon } from '../../../assets/icon/undo.svg'
import { titleName, table, viewId } from '../../../_constants/Code'
import { getDynamoDB } from '../../../common/utils/DynamoDbUtils'
import RelatedDashboard from '../parts/RelatedDashboard'
import { SpinnerButton } from '../../common/CustomButton'
import Forbidden from '../../../_components/Forbidden'
import LoadingMessage from '../../../_components/LoadingMessage'
import { useModal, useModalExtra } from '../../../_components/Modal'
import { openMasterConfirmUpdateModal } from '../../../_components/Modal/useConfirmationModal'
// 組織・階層用のストア（タブ・期間・一覧データ・ソート等）
import {
  DepartmentProvider,
  useDepartmentDispatch,
  useDepartmentSelector,
  TAB_ORG,
  TAB_HIERARCHY,
  MAX_HIERARCHY_LEVELS
} from './extra/store'
// 表示用ヘルパー・定数・カスタムフック・インポート検証（extra/helpers は1本化）
import {
  addDaysToDateString,
  formatPeriodLabel,
  sortDepartmentList,
  toApiDate,
  buildHierarchyIndexList,
  getOrgCodeColumnIndex,
  extractSevenColFromMaster,
  extractSevenColFromMasterTableStyle,
  sortHierarchyMasterOnly,
  buildSyntheticMasterProcessedFromSevenCol,
  DEFAULT_HIERARCHY_HEADERS_GRAPH,
  getUsedDepartmentCodes,
  buildTableDataForRegistration,
  buildHierarchyDataForRegistration,
  normalizeHierarchyLevelValue,
  stripDuplicateNameSuffix,
  validateHierarchyErrors,
  convertHierarchyRowErrorsToTableIndex,
  validateOrgImport,
  validateHierarchyImport,
  BUTTON_STYLES,
  FILE_FORMAT_ERROR_MESSAGE,
  useDepartmentInitialization,
  DELETED_PLACEHOLDER,
  DEPARTMENT_MODAL_STYLE_NARROW_PADDED,
  DEPARTMENT_MODAL_STYLE_NEW_PERIOD,
  DepartmentPeriodChangeUnsavedWarningDescription,
  DepartmentRegisterFlowDeletePeriodConfirmDescription,
  getDepartmentPeriodDeletionConfirmDescription,
  formatDepartmentShortNameForDisplay,
  formatSegmentNameForDisplay,
  buildSegmentMasterValueSet
} from './extra/helpers'
// API呼び出し（期間一覧・組織情報取得・組織階層取得・組織情報更新）
import { getDepartmentInfo, getDepartmentHierarchy, updateDepartmentInfo, deleteDepartmentData } from './extra/services'
import { validateHierarchyRow } from './extra/validation'
import { getMessage } from '../../../common/utils/MessageUtils'
import { DataBridge } from '../../common/DataBridge/DataBridge'
// モーダル（組織登録・編集フォーム、新規期間作成）
import { DepartmentOrgFormModal, useDepartmentOrgFormModal } from './components/DepartmentOrgFormModal'
import { DepartmentNewPeriodModal } from './components/DepartmentNewPeriodModal'
import { HierarchyTable } from './components/HierarchyTable'
import '../../../assets/css/style.css'
import '../../../assets/css/common.css'

/** 組織階層CSV/Excelのヘッダー（DataBridge用・ファイル内のカラム名） */
const HIERARCHY_HEADERS = ['第1階層', '第2階層', '第3階層', '第4階層', '第5階層', '第6階層', '組織コード']

/**
 * Master.RelatedDashboard を RelatedDashboard 用の string[] に揃える（API 生の Dynamo 表現 { S: "x" } も許容）。 RelatedDashboard
 * はマウント時のみ props.dashboards を取り込むため、親側で key 付与と併用する。
 *
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeRelatedDashboardIds(value) {
  if (!value || !Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      if (item != null && typeof item === 'object' && item.S != null) return String(item.S).trim()
      return ''
    })
    .filter(Boolean)
}

/**
 * 組織一覧の「削除」または「復帰」アイコンボタン（B1-005: Content 外に抽出）
 *
 * @param {Object} props
 * @param {boolean} props.isDeleted - 削除済みフラグ
 * @param {boolean} [props.disabled] - 使用中のため削除不可
 * @param {() => void} props.onDelete - 削除時のコールバック
 * @param {() => void} props.onRestore - 復帰時のコールバック
 * @returns {JSX.Element}
 */
function OrgDeleteButton({ isDeleted, disabled = false, onDelete, onRestore }) {
  const disabledClass = '!opacity-40 !cursor-not-allowed !text-gray-400'
  const enabledClass = `!p-0 relative !text-gray-600/50 ${BUTTON_STYLES.deleteButtonHover} hover:before:block`
  const stopAndDelete = (e) => {
    e.stopPropagation()
    onDelete()
  }
  const stopAndRestore = (e) => {
    e.stopPropagation()
    onRestore()
  }
  if (isDeleted) {
    return (
      <IconButton
        title="復帰"
        size="small"
        className={disabled ? `!p-0 relative ${disabledClass}` : enabledClass}
        onClick={stopAndRestore}
        disabled={disabled}
        data-testid="org-restore"
      >
        <UndoIcon className="z-20 w-[20px] h-[20px]" />
      </IconButton>
    )
  }
  if (disabled) {
    return (
      <span title="組織階層で使用中のため削除できません" className="inline-flex">
        <IconButton
          size="small"
          className={`!p-0 relative ${disabledClass}`}
          onClick={stopAndDelete}
          disabled={disabled}
          data-testid="org-delete"
        >
          <DeleteIcon className="z-20" fontSize="small" />
        </IconButton>
      </span>
    )
  }
  return (
    <IconButton
      title="組織削除"
      size="small"
      className={enabledClass}
      onClick={stopAndDelete}
      disabled={disabled}
      data-testid="org-delete"
    >
      <DeleteIcon className="z-20" fontSize="small" />
    </IconButton>
  )
}

/**
 * 組織情報・組織階層設定画面コンテンツ（同一ファイル内で定義） 組織一覧タブ・組織階層タブの切替、期間選択、登録・インポート・エクスポートを担当
 *
 * @param {Object} props
 * @param {string} props.pageTitle - ページタイトル
 * @param {string} props.corporationNumber - 法人番号
 * @param {string} props.corporationName - 法人名
 * @param {string} props.userId - ユーザーID
 * @param {string} props.userName - ユーザー名
 * @param {Object} props.authority - 権限情報
 * @param {string} props.mode - 分析モード
 * @param {string} [props.departmentNameFormat] - Company.Config.CreateDepartmentMaster.DepartmentNameFormat（未指定時は
 *   "({num}){name}"）。階層名のプレフィックス検証に使用
 * @returns {JSX.Element}
 */
function DepInfoHierarchySettingContent(props) {
  const navigate = useNavigate()
  // ---------- ストアから取得する状態 ----------
  const dispatch = useDepartmentDispatch()
  const periodList = useDepartmentSelector((s) => s.periodList) // 対象期間一覧
  const currentPeriodKey = useDepartmentSelector((s) => s.currentPeriodKey) // 選択中期間のキー（開始日）
  const currentDepartments = useDepartmentSelector((s) => s.currentDepartments) // 組織一覧データ
  const currentHierarchy = useDepartmentSelector((s) => s.currentHierarchy) // 組織階層データ（7列形式、登録・エクスポート用）
  const hierarchyMasterList = useDepartmentSelector((s) => s.hierarchyMasterList) // 組織階層 master 2D（API 取得時、表示用）
  const hierarchyCount = useDepartmentSelector((s) => s.hierarchyCount) // 階層列の数（1〜6）
  const activeTab = useDepartmentSelector((s) => s.activeTab) // 表示中タブ（組織一覧 or 組織階層）
  const orgSortKey = useDepartmentSelector((s) => s.orgSortKey) // 組織一覧のソートキー
  const orgSortDir = useDepartmentSelector((s) => s.orgSortDir) // 組織一覧のソート方向（asc/desc）
  const orgDisplayPreserveOrder = useDepartmentSelector((s) => s.orgDisplayPreserveOrder) // true: インポート順で表示、false: 組織コード昇順
  const segmentList = useDepartmentSelector((s) => s.segmentList) // セグメント一覧（組織に紐づける）
  const loading = useDepartmentSelector((s) => s.loading) // ローディング中フラグ（期間切替無効化等）
  const loadingOrg = useDepartmentSelector((s) => s.loadingOrg) // 組織一覧取得中
  const loadingHierarchy = useDepartmentSelector((s) => s.loadingHierarchy) // 組織階層取得中
  const initDone = useDepartmentSelector((s) => s.initDone) // 初期化完了フラグ
  const topError = useDepartmentSelector((s) => s.topError) // 画面上部のエラーメッセージ
  const orgErrorCount = useDepartmentSelector((s) => s.orgErrorCount) // 組織インポート時のエラー件数
  const hierarchyErrorCount = useDepartmentSelector((s) => s.hierarchyErrorCount) // 階層インポート時のエラー件数
  const orgRowErrors = useDepartmentSelector((s) => s.orgRowErrors) || {} // 組織インポート時の行別エラー（行表示用）
  const hierarchyRowErrors = useDepartmentSelector((s) => s.hierarchyRowErrors) || {} // 階層インポート時の行別エラー
  const hierarchyDirty = useDepartmentSelector((s) => s.hierarchyDirty) // 組織階層の未保存変更
  const orgImportReplacedSearchKeys = useDepartmentSelector((s) => s.orgImportReplacedSearchKeys) || [] // インポートで置換されたDB側の組織（登録時に is_delete で送る）
  const hierarchyShowDrilldownSymbols = false // 組織階層は常に master 表示（< > は表示しない）
  const orgFileFormatError = useDepartmentSelector((s) => s.orgFileFormatError) === true // 組織一覧でCSV/Excel以外をアップロードしたとき
  const hierarchyFileFormatError = useDepartmentSelector((s) => s.hierarchyFileFormatError) === true // 組織階層でCSV/Excel以外をアップロードしたとき

  const corporationNumber = props.corporationNumber || ''

  // ---------- バリデーション関数（モーダルフックより前に定義） ----------
  /** 組織階層のバリデーションを実行してストアを更新するヘルパー関数 組織一覧変更時（編集・追加・復元など）に呼び出す */
  const updateHierarchyValidation = React.useCallback(() => {
    if (loadingOrg || loadingHierarchy) return
    // 組織情報の取得失敗などで組織一覧が空のときはクロス検証しない（hierarchyCrossErrors と件数を一致させる）
    if (currentDepartments.length === 0) return
    const { errorCount, rowErrors } = validateHierarchyErrors(
      currentDepartments,
      currentHierarchy,
      hierarchyMasterList,
      hierarchyCount,
      validateHierarchyRow
    )
    dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount, rowErrors })
  }, [
    currentDepartments,
    currentHierarchy,
    hierarchyMasterList,
    hierarchyCount,
    loadingOrg,
    loadingHierarchy,
    dispatch
  ])

  // ---------- モーダル・ref・ローカル状態 ----------
  const showModal = useModal()
  const modalExtra = useModalExtra()
  const { openAddOrgModal, openEditOrgModal } = useDepartmentOrgFormModal({
    showModal,
    modalExtra,
    dispatch,
    segmentList,
    currentDepartments,
    updateHierarchyValidation,
    corporationNumber,
    currentPeriodKey
  })
  const periodButtonRef = React.useRef(null) // 対象期間ドロップダウンボタン
  const [isOpenPeriod, setIsOpenPeriod] = React.useState(false) // 対象期間ドロップダウン開閉
  // 期間変更確認でOK後、共通Modalの閉じる遅延(300ms)が終わるまで新規期間作成を抑止（Modal.jsを触らずに対応）
  const [newPeriodButtonCooldownUntil, setNewPeriodButtonCooldownUntil] = React.useState(0)
  /** AppMaster（マスタ一覧定義）の RelatedDashboard — 他マスタ画面と同様に「適用対象」として表示 */
  const [appMasterMeta, setAppMasterMeta] = React.useState(null)

  React.useEffect(() => {
    document.title = props.pageTitle
  }, [props.pageTitle])

  React.useEffect(() => {
    let cancelled = false
    if (!corporationNumber) return undefined
    getDynamoDB(table.Master, null, corporationNumber, {
      CorporationNumber: corporationNumber,
      MasterId: viewId.depInfoHierarchySetting
    })
      .then((masterData) => {
        if (!cancelled && Array.isArray(masterData) && masterData[0]) {
          setAppMasterMeta(masterData[0])
        }
      })
      .catch(() => {
        // 未取得時は適用対象エリアを出さない（テスト・オフラインでも画面は継続）
      })
    return () => {
      cancelled = true
    }
  }, [corporationNumber])

  // 対象期間ドロップダウンの開閉（開閉時にボタンに is-active を付与）
  const togglePeriodDropdown = React.useCallback((isOpen) => {
    setIsOpenPeriod(isOpen)
    if (periodButtonRef.current) {
      periodButtonRef.current.classList.toggle('is-active', isOpen)
    }
  }, [])

  // 期間変更確認OK後の新規期間作成ボタン cooldown を期限で解除するヘルパー関数
  const startNewPeriodCooldown = React.useCallback((durationMs) => {
    const cooldownUntil = Date.now() + durationMs
    setNewPeriodButtonCooldownUntil(cooldownUntil)
    setTimeout(() => {
      setNewPeriodButtonCooldownUntil(0)
    }, durationMs)
  }, [])

  // ---------- 表示用の派生データ ----------
  // インポート直後はファイル順を保持、API取得後・登録後は組織コード昇順で表示
  const displayDepartments = React.useMemo(() => {
    // インポート直後はファイル順を保持、それ以外はソート
    if (orgDisplayPreserveOrder) {
      return [...currentDepartments]
    }
    return sortDepartmentList(currentDepartments, orgSortKey, orgSortDir)
  }, [currentDepartments, orgSortKey, orgSortDir, orgDisplayPreserveOrder])
  const activeDepCount = React.useMemo(
    () => currentDepartments.filter((d) => (d.status || '') !== 'deleted').length,
    [currentDepartments]
  )
  /** 組織一覧で「まだ残っている」インポートエラー件数（行を削除すると減る）。表示・登録ブロックに使用 */
  const effectiveOrgErrorCount = React.useMemo(() => {
    return (currentDepartments || []).filter((d) => {
      const err = d._importRowIndex != null ? orgRowErrors[String(d._importRowIndex)] : null
      return err && Object.keys(err).length > 0
    }).length
  }, [currentDepartments, orgRowErrors])
  /** 組織階層×組織一覧のクロスバリデーション（B1-003: validateHierarchyErrors に委譲） */
  const hierarchyCrossErrors = React.useMemo(() => {
    if (loadingOrg || loadingHierarchy) return {}
    // 組織情報の取得失敗などで組織一覧が空のときはクロス検証しない。全行が誤って E020004 となり
    // 「組織階層でエラーが発生しています」が二重表示されるのを防ぐ（組織の全削除は status:'deleted' で length>0 のため対象外）
    if (currentDepartments.length === 0) return {}
    const { rowErrors } = validateHierarchyErrors(
      currentDepartments,
      currentHierarchy,
      hierarchyMasterList,
      hierarchyCount,
      validateHierarchyRow
    )
    return rowErrors || {}
  }, [loadingOrg, loadingHierarchy, currentDepartments, currentHierarchy, hierarchyMasterList, hierarchyCount])

  /** 階層インポート時の行エラーとクロスバリデーションをマージした表示用エラー（組織階層タブで使用） */
  const effectiveHierarchyRowErrors = React.useMemo(() => {
    const merged = {}
    const hierarchy = Array.isArray(currentHierarchy) ? currentHierarchy : []

    hierarchy.forEach((_, rowIdx) => {
      const key = String(rowIdx)
      const importErr = hierarchyRowErrors[key]
      const crossErr = hierarchyCrossErrors[key]

      // 両方とも空の場合はスキップ
      if (!importErr && !crossErr) return

      // エラーをマージ（Object.assignの方が高速）
      const combined = Object.assign({}, importErr || {}, crossErr || {})

      if (Object.keys(combined).length > 0) {
        merged[key] = combined
      }
    })

    return merged
  }, [currentHierarchy, hierarchyRowErrors, hierarchyCrossErrors])

  /** 組織階層で「まだ残っている」エラー件数（インポート＋クロス検証）。表示・登録ブロックに使用 */
  const effectiveHierarchyErrorCount = React.useMemo(() => {
    return Object.keys(effectiveHierarchyRowErrors).length
  }, [effectiveHierarchyRowErrors])

  /**
   * 組織一覧が変更されたとき（編集・追加・復元・インポートなど）、組織階層のエラーを再検証してストアを更新する。 組織一覧でデータを補完したあとタブ「組織階層」に戻ったときに不整合エラーが消えるようにする（commit 1803bb6
   * で存在した処理を復元）。 setTimeout 0 で遅延し、削除クリック直後の dispatch が削除状態を上書きしないようにする。
   */
  React.useEffect(() => {
    if (loadingOrg || loadingHierarchy) return
    // 組織一覧が空のときはクロス検証をスキップ（updateHierarchyValidation と件数を一致させる）
    if (currentDepartments.length === 0) return
    const timer = setTimeout(() => {
      updateHierarchyValidation()
    }, 0)
    return () => clearTimeout(timer)
  }, [
    currentDepartments,
    currentHierarchy,
    hierarchyMasterList,
    hierarchyCount,
    loadingOrg,
    loadingHierarchy,
    updateHierarchyValidation
  ])

  /** 組織階層タブ表示用：master 2D（API は drilldown を返さない。インポート時は synthetic） */
  const { displayMasterList, displayProcessedList } = React.useMemo(() => {
    if (Array.isArray(hierarchyMasterList) && hierarchyMasterList.length > 0) {
      const sorted = sortHierarchyMasterOnly(hierarchyMasterList)
      return { displayMasterList: sorted, displayProcessedList: sorted }
    }
    const { masterList: m, processedList: p } = buildSyntheticMasterProcessedFromSevenCol(
      Array.isArray(currentHierarchy) ? currentHierarchy : [],
      hierarchyCount
    )
    return { displayMasterList: m, displayProcessedList: p }
  }, [hierarchyMasterList, currentHierarchy, hierarchyCount])

  /** 組織階層テーブル用ヘッダー（グラフ用列名） */
  const hierarchyTableHeaders = (DEFAULT_HIERARCHY_HEADERS_GRAPH || []).slice(0, hierarchyCount)
  /** 組織階層テーブル用 indexList（表示列インデックス）。master と processed は同じ列構成なので master header から取得 */
  const hierarchyTableIndexList = React.useMemo(
    () => buildHierarchyIndexList(displayMasterList[0], hierarchyCount),
    [displayMasterList, hierarchyCount]
  )
  /** API の master にグラフ用列があるか（synthetic は両方持つ）。ない場合はテーブル body に processed を渡して < > と色を表示する */
  const masterHasGraphColumns = React.useMemo(() => {
    const fromMaster = buildHierarchyIndexList(displayMasterList[0], hierarchyCount)
    return fromMaster.length > 0 && fromMaster.every((i) => i >= 0)
  }, [displayMasterList, hierarchyCount])
  /** HierarchyTable に渡す行データ。常に displayMasterList を渡す（title判定用） */
  const hierarchyTableMasterList = React.useMemo(() => displayMasterList, [displayMasterList])
  /** 組織コード列インデックス（processed のヘッダーから取得） */
  const orgCodeColIdx = React.useMemo(() => getOrgCodeColumnIndex(displayProcessedList[0]), [displayProcessedList])

  /**
   * HierarchyTable 用：effectiveHierarchyRowErrors のキーを 7 列 index (0..6) からテーブルの dataColIndex に変換
   *
   * 7列データ構造: [第1階層, 第2階層, ..., 第6階層, 組織コード] (index 0-6) master/processed構造: [所属組織1(テーブル用), 所属組織1(グラフ用), 所属組織2(テーブル用),
   * 所属組織2(グラフ用), ..., 組織コード, ...]
   *
   * 変換ルール:
   *
   * - 組織コード列（index 6）: そのまま 6 / '6' を保持
   * - 組織名列（index 0-5）: indexList[sevenColIdx] で dataColIdx を取得（グラフ用列のindex）
   */
  const hierarchyRowErrorsForTable = React.useMemo(
    () =>
      convertHierarchyRowErrorsToTableIndex(
        effectiveHierarchyRowErrors,
        hierarchyTableIndexList,
        orgCodeColIdx,
        currentHierarchy
      ),
    [effectiveHierarchyRowErrors, hierarchyTableIndexList, orgCodeColIdx, currentHierarchy]
  )

  /** 組織階層の表示行数（登録数表示・空判定用） */
  const hierarchyDisplayCount =
    displayProcessedList.length > 1 ? displayProcessedList.length - 1 : (currentHierarchy || []).length

  /** 階層エラーがある行の組織コードセット（名前不一致などのエラー行は「使用中」から除外する） */
  const codesWithHierarchyErrors = React.useMemo(() => {
    const codes = new Set()
    const trim = (v) => (v == null ? '' : String(v).trim())
    Object.keys(effectiveHierarchyRowErrors).forEach((key) => {
      const rowIdx = parseInt(key, 10)
      if (isNaN(rowIdx)) return
      const row = currentHierarchy[rowIdx]
      if (!row) return
      const code = trim(row[6])
      if (code) codes.add(code)
    })
    return codes
  }, [effectiveHierarchyRowErrors, currentHierarchy])

  /** 組織階層で使用されている組織コードのセット（状態「使用中」判定用）。各行の「右端の実名」＋組織コードで組織一覧と照合。(N)組織名は親の欠損埋め用プレースホルダなので無視する */
  const departmentCodesUsedInHierarchy = React.useMemo(
    () =>
      getUsedDepartmentCodes(
        currentHierarchy,
        displayProcessedList,
        orgCodeColIdx,
        hierarchyTableIndexList,
        currentDepartments,
        codesWithHierarchyErrors
      ),
    [
      currentHierarchy,
      displayProcessedList,
      orgCodeColIdx,
      hierarchyTableIndexList,
      currentDepartments,
      codesWithHierarchyErrors
    ]
  )

  // 登録・削除ボタンの有効/無効判定
  const selectedPeriod = periodList.find((p) => p.key === currentPeriodKey)
  const selectedPeriodRegistrationStatus = selectedPeriod?.registration_status || 'registered'
  const isLatestPeriod = periodList.length > 0 && periodList[0].key === currentPeriodKey
  const hasPendingDeletes = currentDepartments.some((d) => (d.status || '') === 'deleted')
  const hasData = activeDepCount > 0 || hierarchyDisplayCount > 0 || hasPendingDeletes
  // 全件削除予定かつ非最新期間かつ組織階層タブにデータがない場合のみ登録不可（当該期間が一覧から消えるため）。階層にデータがある場合はボタン有効のまま登録時にエラー表示させる
  const allDeletedNonLatest =
    activeDepCount === 0 && hasPendingDeletes && !isLatestPeriod && hierarchyDisplayCount === 0
  const registerDisabled = !hasData || periodList.length === 0 || allDeletedNonLatest
  // 最新期間なら削除可能（データがなくても新規作成した空の期間を削除できる）
  const deleteDisabled = !isLatestPeriod

  /** 未保存変更の有無（組織の status 変更 / 階層の変更 / 未登録期間） */
  const hasUnsavedChanges = React.useMemo(() => {
    const hasOrgChanges = currentDepartments.some(
      (d) => (d.status || '') === 'new' || (d.status || '') === 'updated' || (d.status || '') === 'deleted'
    )
    const isUnregisteredPeriod = selectedPeriod?.registration_status === 'unregistered'
    return hasOrgChanges || hierarchyDirty || isUnregisteredPeriod
  }, [currentDepartments, hierarchyDirty, selectedPeriod?.registration_status])

  /**
   * 指定期間の組織情報・組織階層をAPIから取得しストアに反映する（Progressive: 組織一覧・組織階層をそれぞれ取得完了次第で表示）
   * エラーが発生しても一方のAPIが成功すればそのデータを表示する（Promise.allSettledを使用）
   *
   * @param {string} periodKey - 期間キー（開始日）
   * @param {{ endDate?: string; hierarchyCount?: number }} [overrides] - オプション（追加時など、selectedPeriod がまだ更新されていない場合に指定）
   */
  const loadPeriodData = React.useCallback(
    async (periodKey, overrides = {}) => {
      if (!periodKey || !corporationNumber) return

      dispatch('SET_LOADING', true)
      dispatch('SET_LOADING_ORG', true)
      dispatch('SET_LOADING_HIERARCHY', true)
      dispatch('SET_TOP_ERROR', '')
      dispatch('SET_ORG_ERROR_COUNT', { errorCount: 0, rowErrors: {} })
      dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount: 0, rowErrors: {} })

      // selectedPeriodやhierarchyCountへの依存を削除し、すべてoverridesで渡す
      const endDate = overrides.endDate ?? ''
      const targetPeriod = { start_date: periodKey, end_date: endDate }
      const hCount = overrides.hierarchyCount ?? MAX_HIERARCHY_LEVELS

      // 組織情報取得（エラーが発生しても階層データは表示可能にするため個別にcatch）
      // API は end_date が空でも DB から正しい値を返すため、override が無い限り空で送る
      const promiseInfo = getDepartmentInfo(corporationNumber, targetPeriod)
        .then((infoRes) => {
          try {
            const infoPayload = infoRes?.body ?? infoRes?.data ?? infoRes
            const targetPeriodInfo = infoPayload?.target_period
            const deptList = Array.isArray(targetPeriodInfo?.department_info)
              ? targetPeriodInfo.department_info.map((d) => ({ ...d, status: d.status || '' }))
              : []
            const segList = Array.isArray(targetPeriodInfo?.segment_list) ? targetPeriodInfo.segment_list : []

            dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
            dispatch('SET_DEPARTMENTS', deptList)
            dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
            dispatch('SET_SEGMENT_LIST', segList)
            // 期間一覧に終了日が無い過去期間でも、IF02074 の target_period.end_date を登録・SF 連携に使えるよう同期する
            const apiEnd = targetPeriodInfo?.end_date
            if (apiEnd != null && String(apiEnd).trim() !== '') {
              const normalizedEnd = toApiDate(apiEnd)
              if (normalizedEnd) {
                dispatch('SYNC_PERIOD_END_DATE_FROM_INFO', { periodKey, endDate: normalizedEnd })
              }
            }
          } catch (err) {
            console.error('組織情報の処理エラー:', err)
            throw err
          }
        })
        .catch((err) => {
          console.error('組織情報の取得エラー:', err)

          // より具体的なエラーメッセージ
          // 英語の err.message は画面表示せず、既定の日本語メッセージにフォールバックする（バグ #3228）
          let errorMessage = '組織情報の取得に失敗しました'
          if (err?.response?.status === 404) {
            errorMessage = '指定された期間の組織情報が見つかりません'
          } else if (err?.response?.status === 403) {
            errorMessage = '組織情報へのアクセス権限がありません'
          }

          dispatch('SET_TOP_ERROR', errorMessage)
          dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
          dispatch('SET_DEPARTMENTS', [])
          dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
          dispatch('SET_SEGMENT_LIST', [])
        })
        .finally(() => dispatch('SET_LOADING_ORG', false))

      // 組織階層取得（エラーが発生しても組織情報は表示可能にするため個別にcatch）
      const promiseHierarchy = getDepartmentHierarchy(corporationNumber, { start_date: periodKey })
        .then((hierRes) => {
          try {
            const hierPayload = hierRes?.body ?? hierRes?.data ?? hierRes
            const targetPeriodHier = hierPayload?.target_period ?? {}
            let masterList = Array.isArray(targetPeriodHier.master) ? targetPeriodHier.master : []

            if (masterList.length === 0) {
              dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
                masterList: [],
                showDrilldownSymbols: false
              })
              dispatch('SET_HIERARCHY', [])
              dispatch('SET_HIERARCHY_COUNT', hCount)
              dispatch('SET_HIERARCHY_DIRTY', false)
              return
            }

            const sortedMaster = sortHierarchyMasterOnly(masterList)
            dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
              masterList: sortedMaster,
              showDrilldownSymbols: false
            })
            let sevenCol = extractSevenColFromMaster(sortedMaster, hCount)
            if (sevenCol.length === 0) {
              sevenCol = extractSevenColFromMasterTableStyle(sortedMaster, hCount)
            }
            dispatch('SET_HIERARCHY', sevenCol)
            dispatch('SET_HIERARCHY_COUNT', hCount)
            dispatch('SET_HIERARCHY_DIRTY', false)
          } catch (err) {
            console.error('組織階層の処理エラー:', err)
            throw err
          }
        })
        .catch((err) => {
          console.error('組織階層の取得エラー:', err)

          // より具体的なエラーメッセージ
          // 英語の err.message は画面表示せず、既定の日本語メッセージにフォールバックする（バグ #3228）
          let errorMessage = '組織階層の取得に失敗しました'
          if (err?.response?.status === 404) {
            errorMessage = '指定された期間の組織階層が見つかりません'
          } else if (err?.response?.status === 403) {
            errorMessage = '組織階層へのアクセス権限がありません'
          }

          dispatch('SET_TOP_ERROR', errorMessage)
          dispatch('SET_HIERARCHY', [])
          dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
            masterList: [],
            showDrilldownSymbols: false
          })
        })
        .finally(() => dispatch('SET_LOADING_HIERARCHY', false))

      // 組織情報と階層は独立した API のため並列実行。片方が 4xx/5xx でも finally でローディングを落とし、成功した側のデータだけ画面に残す設計
      await Promise.allSettled([promiseInfo, promiseHierarchy])
      dispatch('SET_LOADING', false)
    },
    [corporationNumber, dispatch] // 依存配列を最小化
  )

  /** マウント時：期間一覧は DepartmentInfo から取得、DepartmentHierarchyCount は DepartmentHierarchy から取得してマージ */
  const {
    isInitialized,
    error: initError,
    firstPeriodKey
  } = useDepartmentInitialization(corporationNumber, dispatch, TAB_ORG)

  // 初期ロード済みフラグ（初期化時の初回ロードのみ実行する）
  const hasInitialLoadRef = React.useRef(false)
  // 最後にロードした periodKey を保持（重複ロードを防ぐ）
  const lastLoadedPeriodKeyRef = React.useRef('')

  // initDone より後にだけ動かす：期間キーが無い間は loadPeriodData を呼ばない。loading 中は二重フェッチ防止のため待つ
  React.useEffect(() => {
    if (!initDone || periodList.length === 0 || !currentPeriodKey || loading) return

    const period = periodList.find((p) => p.key === currentPeriodKey)
    if (!period) return

    // 初期ロード: まだロードしていない場合
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true
      lastLoadedPeriodKeyRef.current = currentPeriodKey
      loadPeriodData(currentPeriodKey, {
        endDate: '',
        hierarchyCount: period.DepartmentHierarchyCount
      })
      return
    }

    // 期間変更: 前回ロードした periodKey と異なる場合
    if (currentPeriodKey !== lastLoadedPeriodKeyRef.current) {
      lastLoadedPeriodKeyRef.current = currentPeriodKey
      loadPeriodData(currentPeriodKey, {
        endDate: '',
        hierarchyCount: period.DepartmentHierarchyCount
      })
    }
  }, [initDone, periodList, currentPeriodKey, loading, loadPeriodData])

  // 初回マウント時のみタブを「組織一覧」に設定（master から開いたときのデフォルト）
  // 初期化エラーがある場合は表示
  // これらは初期化時に一度だけ実行されるため、useDepartmentInitialization 内で処理

  // ---------- タブ・ソートのハンドラ ----------
  const handleSwitchTab = (tab) => {
    dispatch('SET_ACTIVE_TAB', tab)
  }

  const handleSortOrg = (key) => {
    dispatch('SET_ORG_SORT', { key, dir: orgSortKey === key ? (orgSortDir === 'asc' ? 'desc' : 'asc') : 'asc' })
  }

  /** 組織情報「新規追加」は useDepartmentOrgFormModal の openAddOrgModal を使用（corporationNumber/currentPeriodKey はボタン無効でガード） */

  /** 組織を削除予定にする（ローカル状態のみ、登録ボタンで反映） */
  const handleDeleteOrg = (row) => {
    dispatch('DELETE_DEPARTMENT', { department_code: row.department_code })
    updateHierarchyValidation()
  }

  /** 削除予定の組織を復帰させる */
  const handleRestoreOrg = (row) => {
    dispatch('RESTORE_DEPARTMENT', { department_code: row.department_code })
    updateHierarchyValidation()
  }

  /** 登録ボタン：確認モーダル表示後、IF update_department_info または delete_department_data（全件削除時）を呼び出して組織情報・階層をDBに反映する */
  const handleRegister = React.useCallback(() => {
    const hasPendingDeletesInRegister = currentDepartments.some((d) => (d.status || '') === 'deleted')
    // バリデーション：登録対象が何もない場合のみエラー（全件削除予定の場合は登録可）
    if (activeDepCount === 0 && !hasPendingDeletesInRegister) {
      dispatch('SET_TOP_ERROR', '組織情報を1件以上登録してください')
      window.scrollTo(0, 0)
      return
    }

    if (effectiveOrgErrorCount > 0 || effectiveHierarchyErrorCount > 0) {
      window.scrollTo(0, 0)
      return
    }

    // エラーをクリア
    dispatch('SET_ORG_ERROR_COUNT', { errorCount: 0, rowErrors: {} })
    dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount: 0, rowErrors: {} })
    dispatch('SET_TOP_ERROR', '')

    const startDate = toApiDate(currentPeriodKey) || ''
    const allDeleted = activeDepCount === 0 && hasPendingDeletesInRegister

    // 全件削除予定かつ非最新期間かつ組織階層にデータがない場合は登録不可（ボタン無効化の二重チェック）。階層にデータがある場合は通常のupdateへ進みエラー表示させる
    if (allDeleted && !isLatestPeriod && hierarchyDisplayCount === 0) {
      return
    }

    // 全件削除予定かつ最新期間かつ組織階層にデータがない場合のみ delete_department_data を呼ぶ。階層にデータがある場合は通常のupdateへ進む
    if (allDeleted && isLatestPeriod && hierarchyDisplayCount === 0) {
      const doDeletePeriod = async () => {
        dispatch('SET_LOADING', true)
        dispatch('SET_TOP_ERROR', '')
        try {
          await deleteDepartmentData(corporationNumber, {
            start_date: startDate || currentPeriodKey
          })
          navigate('/master/masterList', {
            state: { message: 'データ反映を開始しました。' }
          })
        } catch (err) {
          // status 400 は業務エラーを日本語表示、それ以外は E011012 へ（バグ #3228）
          const status = err?.response?.status ?? err?.status
          const msg = status === 400 ? '組織情報が使用されているため、削除できません。' : getMessage('E011012')
          dispatch('SET_TOP_ERROR', msg)
        } finally {
          dispatch('SET_LOADING', false)
        }
      }

      showModal({
        title: 'マスター設定値登録の確認',
        description: <DepartmentRegisterFlowDeletePeriodConfirmDescription />,
        isWide: false,
        modalStyle: DEPARTMENT_MODAL_STYLE_NARROW_PADDED,
        isCancel: true,
        onClose: () => {},
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            close: false,
            click: async () => {
              try {
                await doDeletePeriod()
              } finally {
                modalExtra._closeUpModal()
              }
            }
          }
        ]
      })
      return
    }

    // 通常の登録：update_department_info
    const endDate =
      selectedPeriod?.endDate != null && String(selectedPeriod.endDate).trim() !== ''
        ? toApiDate(selectedPeriod.endDate)
        : null

    const table_data = buildTableDataForRegistration(
      currentDepartments,
      startDate,
      endDate,
      segmentList,
      orgImportReplacedSearchKeys
    )

    let hierarchyToSend = currentHierarchy
    if (Array.isArray(hierarchyMasterList) && hierarchyMasterList.length > 0) {
      const sortedMaster = sortHierarchyMasterOnly(hierarchyMasterList)
      let sevenCol = extractSevenColFromMaster(sortedMaster, hierarchyCount)
      if (sevenCol.length === 0) {
        sevenCol = extractSevenColFromMasterTableStyle(sortedMaster, hierarchyCount)
      }
      if (sevenCol.length > 0) hierarchyToSend = sevenCol
    }
    const hierarchy_data = buildHierarchyDataForRegistration(hierarchyToSend)
    const target_period = { start_date: startDate, end_date: endDate }

    openMasterConfirmUpdateModal({
      title: 'マスター設定値登録の確認',
      showModal,
      onClose: () => modalExtra._closeUpModal(),
      onError: (err) => {
        console.error('登録エラー:', err)
        dispatch('SET_TOP_ERROR', err?.message || '登録に失敗しました')
      },
      onClick: async () => {
        dispatch('SET_LOADING', true)
        dispatch('SET_TOP_ERROR', '')

        try {
          await updateDepartmentInfo(corporationNumber, {
            table_data,
            target_period,
            hierarchy_data
          })

          dispatch('UPDATE_PERIOD_REGISTRATION_STATUS', {
            periodKey: currentPeriodKey,
            registration_status: 'registered'
          })

          navigate('/master/masterList', {
            state: { message: 'データ反映を開始しました。' }
          })
        } catch (err) {
          console.error('登録エラー:', err)
          // 英語の err.message は画面表示せず、汎用メッセージ E011012 にフォールバックする（バグ #3228）
          dispatch('SET_TOP_ERROR', getMessage('E011012'))
        } finally {
          dispatch('SET_LOADING', false)
        }
      }
    })
  }, [
    corporationNumber,
    currentDepartments,
    currentHierarchy,
    currentPeriodKey,
    hierarchyMasterList,
    hierarchyCount,
    hierarchyDisplayCount,
    segmentList,
    selectedPeriod,
    activeDepCount,
    isLatestPeriod,
    effectiveOrgErrorCount,
    effectiveHierarchyErrorCount,
    orgImportReplacedSearchKeys,
    dispatch,
    showModal,
    modalExtra,
    navigate
  ])

  /** 削除ボタン（期間全件削除）：常に「最新期間」= periodList[0] を削除対象。登録済・未登録とも同一確認モーダル。登録済→IF、未登録→画面上のみ削除（IFしない） */
  const handleDeletePeriod = React.useCallback(() => {
    if (deleteDisabled || periodList.length === 0) return
    const latestPeriodKey = periodList[0].key
    const latestPeriod = periodList[0]
    const isRegistered = latestPeriod?.registration_status === 'registered'
    const previousPeriodKey = periodList[1]?.key

    showModal({
      title: '組織情報削除の確認',
      description: getDepartmentPeriodDeletionConfirmDescription(isRegistered),
      isWide: true,
      isCancel: true,
      // onClose を渡さない: ボタン押下で modalExtra._closeUpModal() を呼ぶため、onClose で再度 _closeUpModal を渡すとテスト時に再帰でスタックオーバーフローになる
      button: [
        {
          name: '削除を実行',
          className: 'footer-btn button_base red white--text',
          click: async () => {
            if (!isRegistered) {
              dispatch('REMOVE_PERIOD', { key: latestPeriodKey })
              if (previousPeriodKey) {
                dispatch('UPDATE_PERIOD_END_DATE', { periodKey: previousPeriodKey, endDate: '' })
              }
              // lastLoadedPeriodKeyRef は更新しない。effect が currentPeriodKey !== lastLoadedPeriodKeyRef を検知して loadPeriodData を実行するため
              modalExtra._closeUpModal()
              return
            }
            dispatch('SET_LOADING', true)
            dispatch('SET_TOP_ERROR', '')
            try {
              await deleteDepartmentData(corporationNumber, {
                start_date: toApiDate(latestPeriodKey) || latestPeriodKey
              })
              // IF準拠: 削除・ファイル処理完了後はマスター画面へ戻る。navigateを先に実行し画面を離脱させる
              navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
              modalExtra._closeUpModal()
            } catch (err) {
              const status = err?.response?.status ?? err?.status
              const msg =
                status === 400 ? '組織情報が使用されているため、削除できません。' : err?.message || '削除に失敗しました'
              dispatch('SET_TOP_ERROR', msg)
              modalExtra._closeUpModal()
            } finally {
              dispatch('SET_LOADING', false)
            }
          }
        }
      ]
    })
  }, [deleteDisabled, corporationNumber, periodList, dispatch, showModal, modalExtra, navigate])

  /** 新規期間作成モーダルを開く（必要に応じて最新期間のデータを複製できる） */
  const openNewPeriodModal = () => {
    const latestPeriod = periodList[0]
    const minStartDate = latestPeriod?.startDate || ''
    showModal({
      title: '新規期間作成',
      description: '対象期間の開始日を指定し、必要に応じて最新期間のデータを複製できます。',
      isWide: false,
      modalStyle: DEPARTMENT_MODAL_STYLE_NEW_PERIOD,
      isCancel: false,
      body: (
        <DepartmentNewPeriodModal
          key={Date.now()}
          minStartDate={minStartDate}
          existingPeriods={periodList}
          onClose={() => modalExtra._closeUpModal()}
          onCreate={async (startDate, copyLatest) => {
            const isCurrentUnregisteredFirst =
              selectedPeriod?.registration_status === 'unregistered' && periodList[0]?.key === currentPeriodKey
            const previousPeriod = periodList[1]

            const doAddNewPeriodAndLoad = async () => {
              // 新規期間に切り替えるため、前の期間のエラー表示をクリアする（copy時は effect が loadPeriodData を呼ばないためここで明示的にクリア）
              dispatch('SET_TOP_ERROR', '')
              dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount: 0, rowErrors: {} })
              dispatch('SET_ORG_ERROR_COUNT', { errorCount: 0, rowErrors: {} })

              const newPeriod = {
                key: startDate,
                startDate,
                endDate: '',
                label: formatPeriodLabel(startDate, ''),
                DepartmentHierarchyCount: MAX_HIERARCHY_LEVELS,
                registration_status: 'unregistered'
              }
              dispatch('ADD_PERIOD', newPeriod)
              if (latestPeriod) {
                const previousEndDate = addDaysToDateString(startDate, -1)
                if (previousEndDate) {
                  dispatch('UPDATE_PERIOD_END_DATE', { periodKey: latestPeriod.key, endDate: previousEndDate })
                }
              }
              if (copyLatest && latestPeriod) {
                lastLoadedPeriodKeyRef.current = startDate
                const targetPeriod = { start_date: latestPeriod.key, end_date: latestPeriod.endDate || '' }
                try {
                  const [infoRes, hierRes] = await Promise.all([
                    getDepartmentInfo(corporationNumber, targetPeriod),
                    getDepartmentHierarchy(corporationNumber, { start_date: latestPeriod.key })
                  ])
                  const infoPayload = infoRes?.body ?? infoRes?.data ?? infoRes
                  const hierPayload = hierRes?.body ?? hierRes?.data ?? hierRes
                  const targetPeriodInfo = infoPayload?.target_period
                  const deptList = Array.isArray(targetPeriodInfo?.department_info)
                    ? targetPeriodInfo.department_info.map((d) => ({ ...d, status: 'new' }))
                    : []
                  let masterList = Array.isArray(hierPayload?.target_period?.master)
                    ? hierPayload.target_period.master
                    : []
                  const hCount = MAX_HIERARCHY_LEVELS
                  if (masterList.length > 0) {
                    const sortedMaster = sortHierarchyMasterOnly(masterList)
                    // 新規期間コピー時のみ：API がテーブル用ヘッダーで返す場合は TableStyle で抽出。master のみ表示（色・< > なし）
                    let sevenCol = extractSevenColFromMaster(sortedMaster, hCount)
                    if (sevenCol.length === 0) {
                      sevenCol = extractSevenColFromMasterTableStyle(sortedMaster, hCount)
                    }
                    const { masterList: syntheticMaster } = buildSyntheticMasterProcessedFromSevenCol(sevenCol, hCount)
                    dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
                      masterList: syntheticMaster,
                      showDrilldownSymbols: false
                    })
                    dispatch('SET_HIERARCHY', sevenCol)
                    const codesInHierarchy = new Set(
                      sevenCol.map((row) => (row[6] ?? '').toString().trim()).filter(Boolean)
                    )
                    const codesInDeptList = new Set(
                      deptList.map((d) => (d.department_code ?? '').toString().trim()).filter(Boolean)
                    )
                    codesInHierarchy.forEach((code) => {
                      if (!codesInDeptList.has(code)) {
                        deptList.push({
                          department_code: code,
                          department_name: DELETED_PLACEHOLDER,
                          department_short_name: DELETED_PLACEHOLDER,
                          segment_name: DELETED_PLACEHOLDER,
                          status: 'new'
                        })
                        codesInDeptList.add(code)
                      }
                    })
                  } else {
                    dispatch('SET_HIERARCHY_MASTER_PROCESSED', { masterList: [] })
                    dispatch('SET_HIERARCHY', [])
                  }
                  deptList.forEach((r, i) => {
                    r._importRowIndex = i
                  })
                  dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
                  dispatch('SET_DEPARTMENTS', deptList)
                  dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
                  try {
                    const newPeriodTarget = { start_date: startDate, end_date: '' }
                    const newInfoRes = await getDepartmentInfo(corporationNumber, newPeriodTarget)
                    const newInfoPayload = newInfoRes?.body ?? newInfoRes?.data ?? newInfoRes
                    const newTargetPeriodInfo = newInfoPayload?.target_period
                    const segList = Array.isArray(newTargetPeriodInfo?.segment_list)
                      ? newTargetPeriodInfo.segment_list
                      : []
                    dispatch('SET_SEGMENT_LIST', segList)
                    lastLoadedPeriodKeyRef.current = startDate
                    const segmentSet = buildSegmentMasterValueSet(segList)
                    const rowErrors = {}
                    deptList.forEach((d, i) => {
                      const seg = (d.segment_name ?? '').toString().trim()
                      if (seg && !segmentSet.has(seg)) {
                        rowErrors[String(i)] = { segment_name: getMessage('E020004', 'セグメント') }
                      }
                    })
                    dispatch('SET_ORG_ERROR_COUNT', {
                      errorCount: Object.keys(rowErrors).length,
                      rowErrors
                    })
                  } catch (err) {
                    console.error('新規期間のセグメント取得エラー:', err)
                  }
                } catch (err) {
                  console.error('最新期間データ複製エラー:', err)
                }
              } else {
                dispatch('SET_LOADING', true)
                dispatch('SET_TOP_ERROR', '')
                try {
                  const targetPeriod = { start_date: startDate, end_date: '' }
                  const infoRes = await getDepartmentInfo(corporationNumber, targetPeriod)
                  const infoPayload = infoRes?.body ?? infoRes?.data ?? infoRes
                  const targetPeriodInfo = infoPayload?.target_period
                  const deptList = Array.isArray(targetPeriodInfo?.department_info)
                    ? targetPeriodInfo.department_info.map((d) => ({ ...d, status: d.status || '' }))
                    : []
                  const segList = Array.isArray(targetPeriodInfo?.segment_list) ? targetPeriodInfo.segment_list : []
                  dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
                  dispatch('SET_DEPARTMENTS', deptList)
                  dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
                  dispatch('SET_SEGMENT_LIST', segList)
                  dispatch('SET_HIERARCHY', [])
                  lastLoadedPeriodKeyRef.current = startDate
                } catch (err) {
                  dispatch('SET_TOP_ERROR', err?.message || '組織情報の取得に失敗しました')
                  dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
                  dispatch('SET_DEPARTMENTS', [])
                  dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
                  dispatch('SET_HIERARCHY', [])
                } finally {
                  dispatch('SET_LOADING', false)
                }
              }
              modalExtra._closeUpModal()
            }

            if (hasUnsavedChanges) {
              showModal({
                title: '期間変更確認',
                description: <DepartmentPeriodChangeUnsavedWarningDescription />,
                isWide: false,
                modalStyle: DEPARTMENT_MODAL_STYLE_NARROW_PADDED,
                isCancel: true,
                onClose: () => {},
                button: [
                  {
                    name: 'OK',
                    className: 'footer-btn button_base',
                    close: false,
                    click: async () => {
                      // 期間変更確認でOK時も新規期間に切り替えるため、前の期間のエラー表示をクリアする
                      dispatch('SET_TOP_ERROR', '')
                      dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount: 0, rowErrors: {} })
                      dispatch('SET_ORG_ERROR_COUNT', { errorCount: 0, rowErrors: {} })

                      if (isCurrentUnregisteredFirst) {
                        dispatch('REMOVE_UNREGISTERED_PERIOD_AND_REVERT_PREVIOUS', {
                          removedPeriodKey: currentPeriodKey,
                          previousPeriodKey: previousPeriod?.key
                        })
                        const newPeriod = {
                          key: startDate,
                          startDate,
                          endDate: '',
                          label: formatPeriodLabel(startDate, ''),
                          DepartmentHierarchyCount: MAX_HIERARCHY_LEVELS,
                          registration_status: 'unregistered'
                        }
                        dispatch('ADD_PERIOD', newPeriod)
                        if (previousPeriod) {
                          const previousEndDate = addDaysToDateString(startDate, -1)
                          if (previousEndDate) {
                            dispatch('UPDATE_PERIOD_END_DATE', {
                              periodKey: previousPeriod.key,
                              endDate: previousEndDate
                            })
                          }
                        }
                        modalExtra._closeUpModal()
                        if (copyLatest && previousPeriod) {
                          lastLoadedPeriodKeyRef.current = startDate
                          const targetPeriod = {
                            start_date: previousPeriod.key,
                            end_date: previousPeriod.endDate || ''
                          }
                          Promise.all([
                            getDepartmentInfo(corporationNumber, targetPeriod),
                            getDepartmentHierarchy(corporationNumber, { start_date: previousPeriod.key })
                          ])
                            .then(([infoRes, hierRes]) => {
                              const infoPayload = infoRes?.body ?? infoRes?.data ?? infoRes
                              const hierPayload = hierRes?.body ?? hierRes?.data ?? hierRes
                              const targetPeriodInfo = infoPayload?.target_period
                              const deptList = Array.isArray(targetPeriodInfo?.department_info)
                                ? targetPeriodInfo.department_info.map((d) => ({ ...d, status: 'new' }))
                                : []
                              let masterList = Array.isArray(hierPayload?.target_period?.master)
                                ? hierPayload.target_period.master
                                : []
                              const hCount = MAX_HIERARCHY_LEVELS
                              if (masterList.length > 0) {
                                const sortedMaster = sortHierarchyMasterOnly(masterList)
                                // 新規期間コピー時のみ：API がテーブル用ヘッダーで返す場合は TableStyle で抽出。master のみ表示（色・< > なし）
                                let sevenCol = extractSevenColFromMaster(sortedMaster, hCount)
                                if (sevenCol.length === 0) {
                                  sevenCol = extractSevenColFromMasterTableStyle(sortedMaster, hCount)
                                }
                                const { masterList: syntheticMaster } = buildSyntheticMasterProcessedFromSevenCol(
                                  sevenCol,
                                  hCount
                                )
                                dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
                                  masterList: syntheticMaster,
                                  showDrilldownSymbols: false
                                })
                                dispatch('SET_HIERARCHY', sevenCol)
                                const codesInHierarchy = new Set(
                                  sevenCol.map((row) => (row[6] ?? '').toString().trim()).filter(Boolean)
                                )
                                const codesInDeptList = new Set(
                                  deptList.map((d) => (d.department_code ?? '').toString().trim()).filter(Boolean)
                                )
                                codesInHierarchy.forEach((code) => {
                                  if (!codesInDeptList.has(code)) {
                                    deptList.push({
                                      department_code: code,
                                      department_name: DELETED_PLACEHOLDER,
                                      department_short_name: DELETED_PLACEHOLDER,
                                      segment_name: DELETED_PLACEHOLDER,
                                      status: 'new'
                                    })
                                    codesInDeptList.add(code)
                                  }
                                })
                              } else {
                                dispatch('SET_HIERARCHY_MASTER_PROCESSED', { masterList: [] })
                                dispatch('SET_HIERARCHY', [])
                              }
                              deptList.forEach((r, i) => {
                                r._importRowIndex = i
                              })
                              dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
                              dispatch('SET_DEPARTMENTS', deptList)
                              dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
                              return Promise.all([
                                getDepartmentInfo(corporationNumber, {
                                  start_date: startDate,
                                  end_date: ''
                                }),
                                Promise.resolve(deptList)
                              ])
                            })
                            .then(([newInfoRes, deptList]) => {
                              const newInfoPayload = newInfoRes?.body ?? newInfoRes?.data ?? newInfoRes
                              const newTargetPeriodInfo = newInfoPayload?.target_period
                              const segList = Array.isArray(newTargetPeriodInfo?.segment_list)
                                ? newTargetPeriodInfo.segment_list
                                : []
                              dispatch('SET_SEGMENT_LIST', segList)
                              lastLoadedPeriodKeyRef.current = startDate
                              const segmentSet = buildSegmentMasterValueSet(segList)
                              const rowErrors = {}
                              deptList.forEach((d, i) => {
                                const seg = (d.segment_name ?? '').toString().trim()
                                if (seg && !segmentSet.has(seg)) {
                                  rowErrors[String(i)] = { segment_name: getMessage('E020004', 'セグメント') }
                                }
                              })
                              dispatch('SET_ORG_ERROR_COUNT', {
                                errorCount: Object.keys(rowErrors).length,
                                rowErrors
                              })
                            })
                            .catch((err) => {
                              console.error('新規期間データ複製エラー:', err)
                            })
                        } else {
                          dispatch('SET_LOADING', true)
                          dispatch('SET_TOP_ERROR', '')
                          const targetPeriod = { start_date: startDate, end_date: '' }
                          getDepartmentInfo(corporationNumber, targetPeriod)
                            .then((infoRes) => {
                              const infoPayload = infoRes?.body ?? infoRes?.data ?? infoRes
                              const targetPeriodInfo = infoPayload?.target_period
                              const deptList = Array.isArray(targetPeriodInfo?.department_info)
                                ? targetPeriodInfo.department_info.map((d) => ({ ...d, status: d.status || '' }))
                                : []
                              const segList = Array.isArray(targetPeriodInfo?.segment_list)
                                ? targetPeriodInfo.segment_list
                                : []
                              dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', [])
                              dispatch('SET_DEPARTMENTS', deptList)
                              dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', false)
                              dispatch('SET_SEGMENT_LIST', segList)
                              dispatch('SET_HIERARCHY', [])
                              lastLoadedPeriodKeyRef.current = startDate
                            })
                            .catch((err) => {
                              dispatch('SET_TOP_ERROR', err?.message || '組織情報の取得に失敗しました')
                              dispatch('SET_DEPARTMENTS', [])
                              dispatch('SET_HIERARCHY', [])
                            })
                            .finally(() => dispatch('SET_LOADING', false))
                        }
                      } else {
                        await doAddNewPeriodAndLoad()
                        modalExtra._closeUpModal()
                      }
                    }
                  }
                ]
              })
              return
            }

            await doAddNewPeriodAndLoad()
          }}
          onError={() => {}}
        />
      )
    })
  }

  /** DataBridge用：組織一覧のエクスポートデータ（配列オブジェクト） */
  const getOrgRecords = React.useCallback(() => {
    return currentDepartments
      .filter((d) => (d.status || '') !== 'deleted')
      .map((d) => ({
        組織コード: d.department_code ?? '',
        組織名: d.department_name ?? '',
        組織略称: d.department_short_name ?? DELETED_PLACEHOLDER,
        セグメント名: d.segment_name ?? DELETED_PLACEHOLDER
      }))
  }, [currentDepartments])

  /**
   * DataBridge用：組織一覧のインポート（records:
   * DataBridgeがパースした配列オブジェクト）。ファイルのヘッダー名は無視し、列の順序（1列目=組織コード、2列目=組織名、3列目=組織略称、4列目=セグメント）で読み込む。
   */
  const handleOrgImportFromDataBridge = React.useCallback(
    async (records, _fileMeta) => {
      if (!records?.length) return false
      dispatch('SET_ORG_FILE_FORMAT_ERROR', false)
      if (!currentPeriodKey) {
        dispatch('SET_TOP_ERROR', '期間を選択してからインポートしてください')
        return false
      }
      try {
        // ファイルのヘッダー名を無視し、列の順序（index）で読み込む（組織階層タブと同様）
        const fileHeaders = Object.keys(records[0] || {})
        const dataRows = records.map((r) => {
          const row = fileHeaders.map((h) => (r[h] != null ? String(r[h]).trim() : ''))
          while (row.length < 4) row.push('')
          return row.slice(0, 4)
        })
        const fixedHeaders = ['組織コード', '組織名', '組織略称', 'セグメント名']
        const segmentNames = segmentList.map((s) => s.segment_name || s.segment_code || String(s)).filter(Boolean)
        // インポートは「置換」のため、既存テーブルとは比較せず同一ファイル内の重複のみチェック
        const existingCodes = new Set()
        const { validRows, errorCount, rowErrors } = validateOrgImport(
          dataRows,
          fixedHeaders,
          segmentNames,
          existingCodes
        )
        const rowErrorsObj = Object.fromEntries(Array.from(rowErrors.entries()).map(([k, v]) => [String(k), v]))
        dispatch('SET_ORG_ERROR_COUNT', { errorCount, rowErrors: rowErrorsObj })
        validRows.forEach((r, i) => {
          r._importRowIndex = i
          r.status = 'new'
        })
        // インポートで一覧を置換するため、DBにあったがファイルに含まれない組織のみ search_key を記録（登録時に is_delete で送る）。ファイルに含まれるコードは送らない。
        const startDateForImport = toApiDate(currentPeriodKey) || ''
        const newFileCodes = new Set(validRows.map((r) => (r.department_code || '').trim()))
        const replacedSearchKeys = (currentDepartments || [])
          .filter((d) => (d.status || '') !== 'new' && (d.status || '') !== 'deleted')
          .filter((d) => !newFileCodes.has((d.department_code || '').trim()))
          .map((d) => `${startDateForImport}#${d.department_code || ''}`)
        dispatch('SET_ORG_IMPORT_REPLACED_SEARCH_KEYS', replacedSearchKeys)
        dispatch('SET_DEPARTMENTS', validRows)
        dispatch('SET_ORG_DISPLAY_PRESERVE_ORDER', true)
        // Import 後は未登録状態にする（ただし、既に登録済みの期間は状態を変更しない）
        const currentPeriod = periodList.find((p) => p.key === currentPeriodKey)
        if (currentPeriodKey && currentPeriod?.registration_status === 'unregistered') {
          dispatch('UPDATE_PERIOD_REGISTRATION_STATUS', {
            periodKey: currentPeriodKey,
            registration_status: 'unregistered'
          })
        }
        // 組織一覧インポート後、組織階層を新しい組織一覧でre-validate
        if (Array.isArray(currentHierarchy) && currentHierarchy.length > 0) {
          const hierarchyRowErrors = new Map()
          let hierarchyErrorCount = 0
          const formatOpt = { departmentNameFormat: props.departmentNameFormat }
          currentHierarchy.forEach((row, idx) => {
            const err = validateHierarchyRow(row, validRows, formatOpt)
            if (err) {
              hierarchyRowErrors.set(idx, err)
              hierarchyErrorCount += Object.keys(err).length
            }
          })
          const hierarchyRowErrorsObj = Object.fromEntries(
            Array.from(hierarchyRowErrors.entries()).map(([k, v]) => [String(k), v])
          )
          dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount: hierarchyErrorCount, rowErrors: hierarchyRowErrorsObj })
        } else {
          // 組織一覧インポート時は階層バリデーションを更新
          updateHierarchyValidation()
        }
        return true
      } catch (err) {
        dispatch('SET_TOP_ERROR', 'ファイルの読み込みに失敗しました')
        return false
      }
    },
    [segmentList, dispatch, currentPeriodKey, currentDepartments, periodList, updateHierarchyValidation]
  )

  /**
   * DataBridge用：組織階層のエクスポート。master データを出力（- や (N)xxx の fill-in なし）。API 取得時は master から 7 列抽出、インポート時は currentHierarchy の
   *
   * - を空に正規化。
   */
  const hCountForExport = useDepartmentSelector((s) => Math.min(6, Math.max(1, s.hierarchyCount || 6)))
  const hierarchyForExport = useDepartmentSelector((s) => s.currentHierarchy || [])
  const hierarchyMasterListForExport = useDepartmentSelector((s) => s.hierarchyMasterList || [])
  const departmentsForExport = useDepartmentSelector((s) => s.currentDepartments || [])
  const getHierarchyRecords = React.useCallback(() => {
    const hCount = hCountForExport
    const hierarchy = hierarchyForExport
    const hierarchyMasterList = hierarchyMasterListForExport
    // 組織名セット（親組織1件のサフィックス判定に使用）
    const departments = departmentsForExport
    const orgNameSet = new Set(departments.map((d) => d.department_name).filter(Boolean))
    let sevenColRows = []
    let needsSuffixStrip = false
    if (Array.isArray(hierarchyMasterList) && hierarchyMasterList.length > 0) {
      const sortedMaster = sortHierarchyMasterOnly(hierarchyMasterList)
      sevenColRows = extractSevenColFromMasterTableStyle(sortedMaster, hCount)
      if (sevenColRows.length === 0) {
        sevenColRows = extractSevenColFromMaster(sortedMaster, hCount)
      }
      // API取得データ（登録済み）は両列ともサフィックスが含まれる可能性があるため除去する
      needsSuffixStrip = sevenColRows.length > 0
    } else {
      const raw = Array.isArray(hierarchy) ? hierarchy : []
      sevenColRows = raw.map((row) => {
        const r = Array.isArray(row) ? row : []
        const len = r.length
        const out = []
        for (let j = 0; j < len - 1; j++) {
          out.push(normalizeHierarchyLevelValue(r[j]))
        }
        if (len > 0) out.push(String(r[len - 1] ?? '').trim())
        return out
      })
    }
    if (!sevenColRows.length) return []
    // ファイルの再利用のため、常に6列の階層＋組織コードでエクスポート（表示階層数に依存しない）
    // API取得データ（登録済み）の場合は重複回避サフィックス「（親組織名）」を除去。インポートデータはそのまま
    const exportLabels = Array.from({ length: 6 }, (_, i) => `所属組織${i + 1}`).concat('組織コード')
    return sevenColRows.map((row) => {
      const len = row.length
      const orgCode = len > 0 ? (row[len - 1] ?? '').toString().trim() : ''
      const o = {}
      exportLabels.forEach((label, i) => {
        if (i < 6) {
          let v = i < len - 1 ? (row[i] ?? '').toString().trim() : ''
          v = normalizeHierarchyLevelValue(v)
          if (needsSuffixStrip && v) v = stripDuplicateNameSuffix(v, orgNameSet)
          o[label] = v
        } else {
          o[label] = orgCode
        }
      })
      return o
    })
  }, [hCountForExport, hierarchyForExport, hierarchyMasterListForExport, departmentsForExport])

  /** DataBridge用：組織階層のインポート（records: DataBridgeがパースした配列オブジェクト。ファイルのヘッダー名は無視し、列の順序（index）のみを使用して7列のデータとして読み込む） */
  const handleHierarchyImportFromDataBridge = React.useCallback(
    async (records, _fileMeta) => {
      if (!records?.length) return false
      dispatch('SET_HIERARCHY_FILE_FORMAT_ERROR', false)
      try {
        // ファイルのヘッダー名を無視し、列の順序（index）で読み込む
        const fileHeaders = Object.keys(records[0] || {})
        const dataRows = records.map((r) => {
          // 各recordをobjectのvaluesの配列に変換（列順序を保持）
          const row = fileHeaders.map((h) => (r[h] != null ? String(r[h]).trim() : ''))
          // 7列に調整（不足分は空文字、超過分は切り捨て）
          while (row.length < 7) row.push('')
          return row.slice(0, 7)
        })
        const { validRows, errorCount, rowErrors } = validateHierarchyImport(dataRows, currentDepartments, {
          departmentNameFormat: props.departmentNameFormat
        })
        const rowErrorsObj = Object.fromEntries(Array.from(rowErrors.entries()).map(([k, v]) => [String(k), v]))
        dispatch('SET_HIERARCHY_ERROR_COUNT', { errorCount, rowErrors: rowErrorsObj })
        // 先に master を空にする（SET_HIERARCHY_MASTER_PROCESSED は importedHierarchyHeader を null にするため、その後にヘッダーを設定）
        dispatch('SET_HIERARCHY_MASTER_PROCESSED', {
          masterList: [],
          showDrilldownSymbols: false
        })
        dispatch('SET_HIERARCHY_IMPORT_HEADER', HIERARCHY_HEADERS)
        dispatch('SET_HIERARCHY', dataRows)
        dispatch('SET_HIERARCHY_DIRTY', true)
        dispatch('SET_HIERARCHY_COUNT', 6)
        // Import 後は未登録状態にする（ただし、既に登録済みの期間は状態を変更しない）
        const currentPeriod = periodList.find((p) => p.key === currentPeriodKey)
        if (currentPeriodKey && currentPeriod?.registration_status === 'unregistered') {
          dispatch('UPDATE_PERIOD_REGISTRATION_STATUS', {
            periodKey: currentPeriodKey,
            registration_status: 'unregistered'
          })
        }
        return true
      } catch (err) {
        dispatch('SET_TOP_ERROR', 'ファイルの読み込みに失敗しました')
        return false
      }
    },
    [currentDepartments, dispatch, currentPeriodKey, periodList]
  )

  // ---------- レンダリング：ページ全体・ヘッダー・対象期間・タブ・組織/階層コンテンツ・フッターボタン ----------
  return (
    <main className="page-container">
      <section className="page-content">
        <div className="centered-container">
          {/* 上階層に戻る（マスター管理画面に戻る） */}
          <div className="bg main">
            <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
              <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                  <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                  <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                </span>
              </Link>
            </div>
          </div>
          <header className="page-header flex justify-center py-1 pt-8">
            <h1 className="page-header__name text-h1 grey--text py-8">{titleName.depInfoHierarchySetting}</h1>
          </header>
          <div className="page-body">
            {/* 画面上部のエラー表示（年収計算設定の「入力内容に誤りがあります」と同じ位置・同じスタイル：エラー直下にbox、間隔なし） */}
            <div>
              {topError && <div className="red--text">{topError}</div>}
              {effectiveOrgErrorCount + effectiveHierarchyErrorCount > 0 && (
                <>
                  {effectiveOrgErrorCount > 0 && <div className="red--text">組織一覧でエラーが発生しています</div>}
                  {effectiveHierarchyErrorCount > 0 && (
                    <div className="red--text">組織階層でエラーが発生しています</div>
                  )}
                </>
              )}
            </div>

            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-16 px-16 border-blue1">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                {titleName.depInfoHierarchySetting}
              </h2>
              <p className="text-body grey--text mt-4 mb-6">
                組織情報と組織階層の管理を行います。組織の登録・編集、階層構造の設定ができます。
              </p>

              {/* 対象期間のドロップダウン＋新規期間作成ボタン */}
              <div className="box-graph__target-fiscal-year flex items-center gap-4 mt-2 mb-6">
                <div className="text-h5">対象期間：</div>
                <div className="action-area relative z-[25]">
                  <div className="year-container" tabIndex={0} onBlur={() => togglePeriodDropdown(false)}>
                    <div
                      ref={periodButtonRef}
                      id="depPeriodButton"
                      className="yearButton flex items-center justify-between rounded-xl pl-4 pr-2 periodButton py-1"
                      onClick={() => !loading && togglePeriodDropdown(!isOpenPeriod)}
                    >
                      <div className="yearButton__text text-h7 mr-2">
                        {periodList.length === 0
                          ? '（期間がありません）'
                          : (periodList.find((p) => p.key === currentPeriodKey)?.label ?? '（期間がありません）')}
                      </div>
                      <div className="yearButton__icon flex items-center justify-center w-8 h-8">
                        <span className="icon-arrow" />
                      </div>
                    </div>
                    <ul className="year rounded-xl z-10" hidden={!isOpenPeriod}>
                      {periodList.length === 0 ? (
                        <li className="item text-h7 grey--text text-center cursor-default">期間を作成してください</li>
                      ) : (
                        periodList.map((p) => (
                          <li
                            key={p.key}
                            className="item text-h7"
                            onClick={() => {
                              if (p.key === currentPeriodKey) {
                                togglePeriodDropdown(false)
                                return
                              }
                              if (hasUnsavedChanges) {
                                togglePeriodDropdown(false)
                                const nextPeriod = p
                                showModal({
                                  title: '期間変更確認',
                                  description: <DepartmentPeriodChangeUnsavedWarningDescription />,
                                  isWide: false,
                                  modalStyle: DEPARTMENT_MODAL_STYLE_NARROW_PADDED,
                                  isCancel: true,
                                  button: [
                                    {
                                      name: 'OK',
                                      className: 'footer-btn button_base',
                                      close: false, // click内で_closeUpModalを呼ぶため、SpinnerButtonによる二重呼び出しを防ぐ
                                      click: () => {
                                        dispatch('PERIOD_CHANGE_CONFIRMED', {
                                          currentKey: currentPeriodKey,
                                          nextKey: nextPeriod.key
                                        })
                                        modalExtra._closeUpModal()
                                        // PERIOD_CHANGE_CONFIRMED で currentPeriodKey が nextKey に更新されるため、
                                        // effect が自動的に API を呼ぶ（lastLoadedPeriodKeyRef は更新しない）
                                      }
                                    }
                                  ]
                                })
                                return
                              }
                              // SET_CURRENT_PERIOD_KEY を dispatch すると、effect が自動的に API を呼ぶため、ここでは dispatch のみ実行
                              // （重複実行を防ぐため、loadPeriodData は直接呼ばない）
                              dispatch('SET_CURRENT_PERIOD_KEY', p.key)
                              togglePeriodDropdown(false)
                            }}
                          >
                            {p.label}
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
                {/* 新規期間作成：常に有効（初回は0件時、以降は期間一覧取得済みでクリック可能）。期間変更確認OK直後は共通Modalの閉じ遅延に合わせて一時無効 */}
                <button
                  type="button"
                  className="btn-style-primary-action text-h7 min-w-[150px]"
                  onClick={openNewPeriodModal}
                  disabled={Date.now() < newPeriodButtonCooldownUntil}
                >
                  <span className="icon-plus-line icon-32px my-px" aria-hidden />
                  <span className="-translate-y-px">新規期間作成</span>
                </button>
              </div>

              {/* タブ：組織一覧 / 組織階層 */}
              <div className="flex border-b border-gray-200 gap-0">
                <button
                  type="button"
                  className={`flex-1 py-4 px-5 text-body font-medium border-b-2 -mb-0.5 transition ${
                    activeTab === TAB_ORG
                      ? 'blue1--text border-blue1 font-semibold bg-white'
                      : 'text-gray-500 border-transparent hover:blue1--text hover:bg-gray-100'
                  }`}
                  onClick={() => handleSwitchTab(TAB_ORG)}
                >
                  組織一覧
                </button>
                <button
                  type="button"
                  className={`flex-1 py-4 px-5 text-body font-medium border-b-2 -mb-0.5 transition ${
                    activeTab === TAB_HIERARCHY
                      ? 'blue1--text border-blue1 font-semibold bg-white'
                      : 'text-gray-500 border-transparent hover:blue1--text hover:bg-gray-100'
                  }`}
                  onClick={() => handleSwitchTab(TAB_HIERARCHY)}
                >
                  組織階層
                </button>
              </div>

              {activeTab === TAB_ORG && (
                <div className="px-7 pt-6 pb-6 border border-gray-200 border-t-0 bg-white">
                  <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
                    <span className="text-caption-2 font-bold grey--text">登録数：{activeDepCount}件</span>
                    <div className="dep-data-bridge-wrap flex gap-2 items-center">
                      <DataBridge
                        key={`org-databridge-${currentPeriodKey}`}
                        filename={`組織一覧_${(currentPeriodKey || '').replace(/\//g, '-')}`}
                        getRecords={getOrgRecords}
                        onImport={handleOrgImportFromDataBridge}
                        onImportError={(msg) =>
                          dispatch('SET_ORG_FILE_FORMAT_ERROR', msg === FILE_FORMAT_ERROR_MESSAGE)
                        }
                        disabledImport={periodList.length === 0 || !currentPeriodKey}
                        disabledExport={activeDepCount === 0}
                      />
                      <button
                        type="button"
                        className={`btn-style-primary-action text-h7 ${periodList.length === 0 ? 'opacity-50 cursor-default' : ''}`}
                        onClick={openAddOrgModal}
                        disabled={periodList.length === 0}
                      >
                        <span className="icon-plus-line icon-32px my-px" aria-hidden />
                        <span className="-translate-y-px">新規追加</span>
                      </button>
                    </div>
                  </div>
                  {(effectiveOrgErrorCount > 0 || orgFileFormatError) && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-body font-medium">
                      {orgFileFormatError
                        ? FILE_FORMAT_ERROR_MESSAGE
                        : `${effectiveOrgErrorCount}件のエラーが検出されました。以下のセルを確認してください。`}
                    </div>
                  )}
                  <div className="min-h-[400px] max-h-[560px] overflow-y-auto border border-gray-200 rounded-lg">
                    <table
                      className={`base-table w-full table-fixed text-caption-2 [&_thead_th]:!border-0 [&_tbody]:!border-0 [&_tbody_td]:!border-0 [&_thead_th]:!border-b [&_tbody_td]:!border-b [&_thead_th]:!border-gray-200 [&_tbody_td]:!border-gray-100 [&_thead_th]:!py-3 [&_thead_th]:!px-5 [&_tbody_td]:!py-3 [&_tbody_td]:!px-5 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20 [&_thead_th]:!bg-[#e0e5f2] ${displayDepartments.length === 0 ? '[&_thead_th]:!border-b-0' : ''}`}
                    >
                      <thead>
                        <tr>
                          <th className="base-table-header min-w-[160px] w-[160px]">
                            <button
                              type="button"
                              className="w-full text-left cursor-pointer select-none hover:opacity-80 bg-transparent border-none p-0"
                              onClick={() => handleSortOrg('department_code')}
                              aria-label={`組織コードで${orgSortKey === 'department_code' && orgSortDir === 'asc' ? '降順' : '昇順'}にソート`}
                              aria-sort={
                                orgSortKey === 'department_code'
                                  ? orgSortDir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <span className="inline-flex items-center gap-2">
                                組織コード
                                <span className="inline-flex flex-col -space-y-1.5 text-[8px]" aria-hidden="true">
                                  <span
                                    className={
                                      orgSortKey === 'department_code' && orgSortDir === 'asc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▲
                                  </span>
                                  <span
                                    className={
                                      orgSortKey === 'department_code' && orgSortDir === 'desc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▼
                                  </span>
                                </span>
                              </span>
                            </button>
                          </th>
                          <th className="base-table-header min-w-[220px] w-[220px]">
                            <button
                              type="button"
                              className="w-full text-left cursor-pointer select-none hover:opacity-80 bg-transparent border-none p-0"
                              onClick={() => handleSortOrg('department_name')}
                              aria-label={`組織名で${orgSortKey === 'department_name' && orgSortDir === 'asc' ? '降順' : '昇順'}にソート`}
                              aria-sort={
                                orgSortKey === 'department_name'
                                  ? orgSortDir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <span className="inline-flex items-center gap-2">
                                組織名
                                <span className="inline-flex flex-col -space-y-1.5 text-[8px]" aria-hidden="true">
                                  <span
                                    className={
                                      orgSortKey === 'department_name' && orgSortDir === 'asc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▲
                                  </span>
                                  <span
                                    className={
                                      orgSortKey === 'department_name' && orgSortDir === 'desc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▼
                                  </span>
                                </span>
                              </span>
                            </button>
                          </th>
                          <th className="base-table-header min-w-[170px] w-[170px]">
                            <button
                              type="button"
                              className="w-full text-left cursor-pointer select-none hover:opacity-80 bg-transparent border-none p-0"
                              onClick={() => handleSortOrg('department_short_name')}
                              aria-label={`組織略称で${orgSortKey === 'department_short_name' && orgSortDir === 'asc' ? '降順' : '昇順'}にソート`}
                              aria-sort={
                                orgSortKey === 'department_short_name'
                                  ? orgSortDir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <span className="inline-flex items-center gap-2">
                                組織略称
                                <span className="inline-flex flex-col -space-y-1.5 text-[8px]" aria-hidden="true">
                                  <span
                                    className={
                                      orgSortKey === 'department_short_name' && orgSortDir === 'asc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▲
                                  </span>
                                  <span
                                    className={
                                      orgSortKey === 'department_short_name' && orgSortDir === 'desc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▼
                                  </span>
                                </span>
                              </span>
                            </button>
                          </th>
                          <th className="base-table-header min-w-[180px] w-[180px]">
                            <button
                              type="button"
                              className="w-full text-left cursor-pointer select-none hover:opacity-80 bg-transparent border-none p-0"
                              onClick={() => handleSortOrg('segment_name')}
                              aria-label={`セグメント名で${orgSortKey === 'segment_name' && orgSortDir === 'asc' ? '降順' : '昇順'}にソート`}
                              aria-sort={
                                orgSortKey === 'segment_name'
                                  ? orgSortDir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <span className="inline-flex items-center gap-2">
                                セグメント名
                                <span className="inline-flex flex-col -space-y-1.5 text-[8px]" aria-hidden="true">
                                  <span
                                    className={
                                      orgSortKey === 'segment_name' && orgSortDir === 'asc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▲
                                  </span>
                                  <span
                                    className={
                                      orgSortKey === 'segment_name' && orgSortDir === 'desc'
                                        ? 'opacity-100'
                                        : 'opacity-40'
                                    }
                                  >
                                    ▼
                                  </span>
                                </span>
                              </span>
                            </button>
                          </th>
                          <th className="base-table-header w-20">状態</th>
                          <th className="base-table-header w-16">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!initDone || loadingOrg ? (
                          <tr className="[&_td]:!border-0">
                            <td colSpan={6} className="!border-0">
                              <LoadingMessage
                                message="データを読み込み中..."
                                minHeightClassName="min-h-[200px]"
                                className="py-8"
                              />
                            </td>
                          </tr>
                        ) : displayDepartments.length === 0 ? (
                          <tr className="[&_td]:!border-0">
                            <td colSpan={6} className="text-center px-5 align-middle !border-0">
                              <div className="min-h-[360px] flex flex-col items-center justify-center">
                                <p className="text-caption-1 text-gray-600">
                                  インポートまたは「＋ 新規追加」でデータを登録してください。
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          displayDepartments.map((d, idx) => {
                            const isDeleted = (d.status || '') === 'deleted'
                            const rowErrs = d._importRowIndex != null ? orgRowErrors[String(d._importRowIndex)] : null
                            return (
                              <tr
                                key={`org-${idx}-${d.department_code ?? ''}`}
                                className={isDeleted ? 'bg-gray-100 opacity-60' : ''}
                              >
                                <td
                                  className={`overflow-hidden ${isDeleted ? 'line-through grey--text' : ''} ${rowErrs?.department_code ? 'bg-red-50' : ''}`}
                                >
                                  <span className="block truncate" title={d.department_code ?? ''}>
                                    {d.department_code}
                                  </span>
                                  {rowErrs?.department_code && (
                                    <span className="block text-caption-2 red--text mt-0.5">
                                      {rowErrs.department_code}
                                    </span>
                                  )}
                                </td>
                                <td className={`overflow-hidden ${rowErrs?.department_name ? 'bg-red-50' : ''}`}>
                                  {(d.status || '') === 'deleted' ? (
                                    <span
                                      className="block truncate grey--text line-through"
                                      title={d.department_name ?? ''}
                                    >
                                      {d.department_name}
                                    </span>
                                  ) : (
                                    <Link
                                      component="button"
                                      type="button"
                                      className="text-caption-2 w-full truncate !text-left !no-underline block"
                                      title={d.department_name ?? ''}
                                      onClick={() => openEditOrgModal(d)}
                                    >
                                      {d.department_name}
                                    </Link>
                                  )}
                                  {rowErrs?.department_name && (
                                    <span className="block text-caption-2 red--text mt-0.5">
                                      {rowErrs.department_name}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={`overflow-hidden ${isDeleted ? 'line-through grey--text' : ''} ${rowErrs?.department_short_name ? 'bg-red-50' : ''}`}
                                >
                                  <span
                                    className="block truncate"
                                    title={formatDepartmentShortNameForDisplay(d.department_short_name)}
                                  >
                                    {formatDepartmentShortNameForDisplay(d.department_short_name)}
                                  </span>
                                  {rowErrs?.department_short_name && (
                                    <span className="block text-caption-2 red--text mt-0.5">
                                      {rowErrs.department_short_name}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={`overflow-hidden ${isDeleted ? 'line-through grey--text' : ''} ${rowErrs?.segment_name ? 'bg-red-50' : ''}`}
                                >
                                  <span className="block truncate" title={formatSegmentNameForDisplay(d.segment_name)}>
                                    {formatSegmentNameForDisplay(d.segment_name)}
                                  </span>
                                  {rowErrs?.segment_name && (
                                    <span className="block text-caption-2 red--text mt-0.5">
                                      {rowErrs.segment_name}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {(d.status || '') === 'deleted' ? (
                                    '削除予定'
                                  ) : loadingHierarchy ? (
                                    '読込中'
                                  ) : departmentCodesUsedInHierarchy.has(
                                      (d.department_code ?? '').toString().trim()
                                    ) ? (
                                    <span className="text-lime-600 font-medium">使用中</span>
                                  ) : (
                                    '未使用'
                                  )}
                                </td>
                                <td>
                                  <OrgDeleteButton
                                    isDeleted={isDeleted}
                                    disabled={departmentCodesUsedInHierarchy.has(
                                      (d.department_code ?? '').toString().trim()
                                    )}
                                    onDelete={() => handleDeleteOrg(d)}
                                    onRestore={() => handleRestoreOrg(d)}
                                  />
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-caption-2 grey--text mt-4 pl-1">
                    ※
                    インポートまたは新規登録後、「登録」ボタンを押すとDBに反映されます。ヘッダーをクリックでソートできます。
                  </p>
                </div>
              )}

              {activeTab === TAB_HIERARCHY && (
                <div className="px-7 pt-6 pb-6 border border-gray-200 border-t-0 bg-white">
                  <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
                    <span className="text-caption-2 font-bold grey--text">登録数：{hierarchyDisplayCount}件</span>
                    <div className="dep-data-bridge-wrap flex gap-2 items-center">
                      <DataBridge
                        key={`hierarchy-databridge-${currentPeriodKey}`}
                        filename={`組織階層_${(currentPeriodKey || '').replace(/\//g, '-')}`}
                        getRecords={getHierarchyRecords}
                        onImport={handleHierarchyImportFromDataBridge}
                        onImportError={(msg) =>
                          dispatch('SET_HIERARCHY_FILE_FORMAT_ERROR', msg === FILE_FORMAT_ERROR_MESSAGE)
                        }
                        disabledImport={periodList.length === 0}
                        disabledExport={hierarchyDisplayCount === 0}
                      />
                    </div>
                  </div>
                  {(effectiveHierarchyErrorCount > 0 || hierarchyFileFormatError) && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded red--text text-body font-medium">
                      {hierarchyFileFormatError
                        ? FILE_FORMAT_ERROR_MESSAGE
                        : `${effectiveHierarchyErrorCount}件のエラーが検出されました。以下のセルを確認してください。`}
                    </div>
                  )}
                  <div className="min-h-[320px] max-h-[600px] overflow-hidden border border-gray-200 rounded-lg mt-4 relative">
                    {!initDone || loadingHierarchy ? (
                      <div className="min-h-[320px] flex items-center justify-center">
                        <LoadingMessage
                          message="データを読み込み中..."
                          minHeightClassName="min-h-[150px]"
                          className="py-8"
                        />
                      </div>
                    ) : (
                      <>
                        {hierarchyDisplayCount === 0 && (
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-caption-1 text-gray-600 pointer-events-none z-10">
                            インポートで組織階層データを登録してください。
                          </div>
                        )}
                        {/* API master 取得時もインポートプレビューも同一 HierarchyTable（列幅・ヘッダー・セル表現を統一） */}
                        <div className="hierarchy-table-wrapper [&_[class*='virtualized-table']]:!border-0 [&_[class*='headerRow']]:!border-0 [&_[class*='headerRow']]:!border-b [&_[class*='headerRow']]:!border-gray-200 [&_[class*='headerColumn']]:!border-r-0">
                          <HierarchyTable
                            key={`hierarchy-${currentPeriodKey}-${Object.keys(hierarchyRowErrorsForTable).length}`}
                            masterList={hierarchyTableMasterList}
                            processedList={displayProcessedList}
                            headers={hierarchyTableHeaders}
                            indexList={hierarchyTableIndexList}
                            targetStartDate={currentPeriodKey}
                            orgCodeColumnIndex={orgCodeColIdx >= 0 ? orgCodeColIdx : undefined}
                            showDrilldownSymbols={hierarchyShowDrilldownSymbols}
                            rowErrors={hierarchyRowErrorsForTable}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-caption-2 grey--text mt-4 pl-1">
                    ※ 組織階層の編集はファイルのインポートで行ってください
                  </p>
                </div>
              )}

              <div className="content-footer mt-16 mb-8">
                <div className="border-t border-blue1" />
                <div className="footer_button_area mt-8 flex justify-between items-center">
                  <Link
                    color="inherit"
                    variant="button"
                    underline="none"
                    component={RouterLink}
                    to="/master/masterList"
                  >
                    <button type="button" className="footer-btn button_back">
                      キャンセル
                    </button>
                  </Link>
                  <SpinnerButton
                    name="登録"
                    className="footer-btn button_base"
                    disabled={registerDisabled}
                    onClick={handleRegister}
                  />
                  <button
                    type="button"
                    className={`footer-btn button_base red white--text ${deleteDisabled ? 'opacity-50 cursor-default pointer-events-none' : ''}`}
                    disabled={deleteDisabled}
                    onClick={handleDeletePeriod}
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
            {/* 適用対象（EmploymentTypeInfo 同様、メインボックス外の独立カード） */}
            <RelatedDashboard
              key={
                appMasterMeta
                  ? `dep-related-dashboard-${normalizeRelatedDashboardIds(appMasterMeta.RelatedDashboard).join('|')}`
                  : 'dep-related-dashboard-pending'
              }
              dashboards={normalizeRelatedDashboardIds(appMasterMeta?.RelatedDashboard)}
              authority={props.authority}
              mode={props.mode}
            />
          </div>
        </div>
      </section>
    </main>
  )
}

/**
 * 組織情報・組織階層設定画面（エントリ＋権限チェック）
 *
 * @returns {JSX.Element}
 */
function DepInfoHierarchySetting() {
  const state = useSelector((s) => s)
  const navigate = useNavigate()
  // 権限がない場合は403画面を表示
  if (
    !state.authority?.Master_AuthInfo?.Availability ||
    !state.authority?.Master_AuthInfo?.Auth_Conf?.Department?.DepartmentData
  ) {
    return <Forbidden />
  }
  const departmentNameFormat = state.company?.config?.CreateDepartmentMaster?.DepartmentNameFormat ?? '({num}){name}'
  return (
    <DepartmentProvider>
      <DepInfoHierarchySettingContent
        pageTitle={titleName.depInfoHierarchySetting + titleName.common}
        corporationNumber={state.company?.corporationNumber}
        corporationName={state.company?.name}
        userId={state.user?.sub}
        userName={state.user?.userName}
        authority={state.authority}
        mode={state.analysis?.mode}
        departmentNameFormat={departmentNameFormat}
      />
    </DepartmentProvider>
  )
}

export default DepInfoHierarchySetting
